import { mkdir, readFile, writeFile } from 'node:fs/promises';
import path from 'node:path';
import {
  type CompactionRecord,
  CompactionRecordSchema,
  type CompactionStore,
} from './types';

export class InMemoryCompactionStore implements CompactionStore {
  readonly #records = new Map<string, CompactionRecord[]>();

  async getRecords(sessionId: string): Promise<CompactionRecord[]> {
    return (this.#records.get(sessionId) ?? []).map((record) =>
      structuredClone(record),
    );
  }

  async appendRecords(
    sessionId: string,
    records: CompactionRecord[],
  ): Promise<void> {
    if (records.length === 0) {
      return;
    }
    const current = this.#records.get(sessionId) ?? [];
    this.#records.set(sessionId, [
      ...current,
      ...records.map((record) => structuredClone(record)),
    ]);
  }
}

export class FileCompactionStore implements CompactionStore {
  constructor(
    private readonly rootDir = path.join(
      process.cwd(),
      '.agents',
      'compaction',
    ),
  ) {}

  async getRecords(sessionId: string): Promise<CompactionRecord[]> {
    const file = this.#recordsPath(sessionId);
    let contents: string;
    try {
      contents = await readFile(file, 'utf8');
    } catch (error) {
      if (
        error &&
        typeof error === 'object' &&
        (error as { code?: unknown }).code === 'ENOENT'
      ) {
        return [];
      }
      throw error;
    }

    const records: CompactionRecord[] = [];
    for (const line of contents.split('\n')) {
      const trimmed = line.trim();
      if (!trimmed) {
        continue;
      }
      records.push(CompactionRecordSchema.parse(JSON.parse(trimmed)));
    }
    return records;
  }

  async appendRecords(
    sessionId: string,
    records: CompactionRecord[],
  ): Promise<void> {
    if (records.length === 0) {
      return;
    }
    const file = this.#recordsPath(sessionId);
    await mkdir(path.dirname(file), { recursive: true });
    const existing = await this.getRecords(sessionId);
    const lines = [...existing, ...records].map((record) =>
      JSON.stringify(CompactionRecordSchema.parse(record)),
    );
    await writeFile(file, `${lines.join('\n')}\n`, 'utf8');
  }

  #recordsPath(sessionId: string): string {
    return path.join(
      this.rootDir,
      encodeURIComponent(sessionId),
      'records.jsonl',
    );
  }
}
